import { FunctionComponent, useCallback, useState } from "react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { ManagementPeriodMode } from "../utils/management-period-picker.utils";
import { MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX } from "../utils/management-toolbar.constants";
import { ManagementPeriodPickerDialog } from "./ManagementPeriodPickerDialog";
import { ManagementToolbarField } from "./ManagementToolbarField";

type ManagementPeriodNavigationProps = {
  periodMode: ManagementPeriodMode;
  periodAnchor: DateTime;
  periodLabel: string;
  onPrevious: () => void;
  onNext: () => void;
  onSelect: (mode: ManagementPeriodMode, anchor: DateTime) => void;
};

/**
 * Soft period strip: plain chevrons + text date (opens picker). One shared surface, no mixed button chrome.
 */
export const ManagementPeriodNavigation: FunctionComponent<ManagementPeriodNavigationProps> = ({
  periodMode,
  periodAnchor,
  periodLabel,
  onPrevious,
  onNext,
  onSelect
}) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleOpenPicker = useCallback(() => {
    setPickerOpen(true);
  }, []);

  const handleClosePicker = useCallback(() => {
    setPickerOpen(false);
  }, []);

  return (
    <>
      <ManagementToolbarField
        label={t("_management_period")}
        labelId="management-period-field-label"
      >
        <Box
          data-testid="management-period-controls"
          role="group"
          aria-labelledby="management-period-field-label"
          sx={theme => ({
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.04)
                : theme.palette.grey[50],
            overflow: "hidden"
          })}
        >
          <IconButton
            aria-label={t("_management_previousPeriod")}
            onClick={onPrevious}
            size="small"
            sx={{
              borderRadius: 0,
              width: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
              height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
              flexShrink: 0
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <ButtonBase
            onClick={handleOpenPicker}
            data-testid="management-period-label"
            aria-label={t("_management_periodPickerOpen", { period: periodLabel })}
            aria-haspopup="dialog"
            sx={theme => ({
              flex: 1,
              minWidth: 0,
              height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
              px: 1,
              py: 0.5,
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderRight: `1px solid ${theme.palette.divider}`,
              "&:hover": {
                bgcolor: "action.hover"
              }
            })}
          >
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                textAlign: "center",
                textWrap: "balance",
                width: "100%"
              }}
            >
              {periodLabel}
            </Typography>
          </ButtonBase>
          <IconButton
            aria-label={t("_management_nextPeriod")}
            onClick={onNext}
            size="small"
            sx={{
              borderRadius: 0,
              width: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
              height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
              flexShrink: 0
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </ManagementToolbarField>

      <ManagementPeriodPickerDialog
        open={pickerOpen}
        periodMode={periodMode}
        periodAnchor={periodAnchor}
        onClose={handleClosePicker}
        onSelect={onSelect}
      />
    </>
  );
};
